import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TopMenu from '../../contexts/TopMenu';
import { authService } from '../../services/auth';
import { API_BASE_URL } from '../../config';
import '../../styles/Fulfillment.css';

const statusSteps = [
  { key: 'confirmed', label: 'CONFIRMED', icon: (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="black"/>
      <path d="M10 16.5L14 20.5L22 12.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ) },
  { key: 'processed', label: 'PROCESSED', icon: (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="15" stroke="black" strokeWidth="2"/>
      <path d="M16 10V16L20 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="16" cy="16" r="6" stroke="black" strokeWidth="2"/>
    </svg>
  ) },
  { key: 'dispatched', label: 'DISPATCHED', icon: (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="15" stroke="black" strokeWidth="2"/>
      <rect x="10" y="14" width="8" height="6" rx="1" stroke="black" strokeWidth="2"/>
      <rect x="18" y="16" width="6" height="4" rx="1" stroke="black" strokeWidth="2"/>
      <circle cx="13" cy="21" r="1" fill="black"/>
      <circle cx="22" cy="21" r="1" fill="black"/>
    </svg>
  ) },
  { key: 'delivered', label: 'DELIVERED', icon: (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="15" stroke="black" strokeWidth="2"/>
      <path d="M10 18L16 12L22 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="12" y="18" width="8" height="4" rx="1" stroke="black" strokeWidth="2"/>
    </svg>
  ) },
];

function getCurrentStep(item) {
  const status = (item?.status || item?.fulfillment_status || '').toLowerCase();
  if (['delivered', 'completed'].includes(status)) return 3;
  if (['dispatched', 'shipped', 'in_transit', 'out_for_delivery'].includes(status)) return 2;
  if (['processed', 'processing', 'packed', 'ready_to_ship'].includes(status)) return 1;
  return 0; // confirmed / placed / paid
}

function OrderStatusTracker({ item, order }) {
  const currentStep = getCurrentStep(item);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40, marginTop: 10 }}>
      {statusSteps.map((step, idx) => {
        const isActive = idx === currentStep;
        const isCompleted = idx <= currentStep;
        const isFilled = isActive || isCompleted;
        const iconColor = isFilled ? 'white' : 'black';
        
        const getIcon = () => {
          if (step.key === 'confirmed') {
            return (
              <svg width="48" height="48" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="16" fill={isFilled ? 'black' : 'white'} stroke={isFilled ? 'none' : 'black'} strokeWidth="2"/>
                <path d="M10 16.5L14 20.5L22 12.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            );
          }
          if (step.key === 'processed') {
            return (
              <svg width="48" height="48" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="15" fill={isFilled ? 'black' : 'white'} stroke={isFilled ? 'none' : 'black'} strokeWidth="2"/>
                <path d="M16 10V16L20 18" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="16" cy="16" r="6" stroke={iconColor} strokeWidth="2"/>
              </svg>
            );
          }
          if (step.key === 'dispatched') {
            return (
              <svg width="48" height="48" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="15" fill={isFilled ? 'black' : 'white'} stroke={isFilled ? 'none' : 'black'} strokeWidth="2"/>
                <rect x="10" y="14" width="8" height="6" rx="1" stroke={iconColor} strokeWidth="2"/>
                <rect x="18" y="16" width="6" height="4" rx="1" stroke={iconColor} strokeWidth="2"/>
                <circle cx="13" cy="21" r="1" fill={iconColor}/>
                <circle cx="22" cy="21" r="1" fill={iconColor}/>
              </svg>
            );
          }
          if (step.key === 'delivered') {
            return (
              <svg width="48" height="48" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="15" fill={isFilled ? 'black' : 'white'} stroke={isFilled ? 'none' : 'black'} strokeWidth="2"/>
                <path d="M10 18L16 12L22 18" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="12" y="18" width="8" height="4" rx="1" stroke={iconColor} strokeWidth="2"/>
              </svg>
            );
          }
        };

        return (
          <React.Fragment key={step.key}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isActive ? '0 0 12px 2px #ffe082' : 'none',
                marginBottom: 8,
                transition: 'all 0.2s',
                borderRadius: '50%',
              }}>
                {getIcon()}
              </div>
              <span style={{
                marginTop: 4,
                fontSize: 14,
                fontWeight: 500,
                color: isFilled ? '#000' : '#aaa',
                letterSpacing: 2,
                textTransform: 'uppercase',
                opacity: isFilled ? 1 : 0.5,
              }}>{step.label}</span>
            </div>
            {idx < statusSteps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: 'linear-gradient(90deg, #f3eaff 0%, #e3eaff 100%)', margin: '0 0px 0 0px' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function OrderItemDetails({ email, onLogout }) {
  const { orderId, itemHandle, itemVariantId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [awb, setAwb] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [productDetails, setProductDetails] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState('');

  const handleFormSubmit = async (e, step) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const authKey = localStorage.getItem('authKey');
      const userId = localStorage.getItem('userId');

      if (!authKey || !userId) {
        throw new Error('Authentication credentials not found');
      }

      let payload = {
        auth_key: authKey,
        user_id: userId,
        order_id: orderId,
        item_handle: itemHandle,
        item_variant_id: itemVariantId
      };

      // Add step-specific data
      switch (step) {
        case 'confirmed':
          if (!purchaseAmount) {
            throw new Error('Purchase amount is required');
          }
          payload.status = 'processed';
          payload.purchase_amount = parseFloat(purchaseAmount);
          break;
        case 'processed':
          if (!awb) {
            throw new Error('AWB number is required');
          }
          payload.status = 'dispatched';
          payload.awb_code = awb;
          break;
        case 'dispatched':
          payload.status = 'delivered';
          break;
        default:
          throw new Error('Invalid step');
      }

      console.log(payload);
      const response = await fetch(`${API_BASE_URL}/fulfill/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update order');
      }

      // Clear form data
      setPurchaseAmount('');
      setAwb('');

      // Reload the entire component data
      setLoading(true);
      try {
        // Fetch order details
        const response = await authService.getOrders();
        const orderData = response.data.find(o => o.order_id === orderId);
        
        if (!orderData) {
          throw new Error('Order not found');
        }

        setOrder(orderData);
        
        // Find the specific item
        const itemData = (orderData.items || []).find(i => 
          (i.product_id === itemHandle || i.product_handle === itemHandle || i.name === itemHandle) &&
          (i.variant_id === itemVariantId || i.variant_title === itemVariantId || !itemVariantId)
        ) || (orderData.items && orderData.items[0]);

        if (!itemData) {
          throw new Error('Item not found');
        }

        setItem(itemData);
      } catch (error) {
        console.error('Error reloading order item:', error);
        setError(error.message || 'Failed to reload order item');
      } finally {
        setLoading(false);
      }

    } catch (err) {
      console.error('Error updating order:', err);
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleProductLinkOpen = () => {
    if (productDetails.url) window.open(productDetails.url, '_blank');
  };

  // Opens the alternate product URL, if available
  const handleAltProductLinkOpen = () => {
    if (productDetails.alt_url) window.open(productDetails.alt_url, '_blank');
  };

  const handleConfirmedSubmit = (e) => handleFormSubmit(e, 'confirmed');
  const handleProcessedSubmit = (e) => handleFormSubmit(e, 'processed');
  const handleMarkDelivered = () => handleFormSubmit({ preventDefault: () => {} }, 'dispatched');

  function getStepKey(order) {
    const status = (order?.status || '').toLowerCase();
    if (['delivered', 'completed'].includes(status)) return 'delivered';
    if (['dispatched', 'shipped', 'in_transit'].includes(status)) return 'dispatched';
    if (['processed', 'processing', 'packed'].includes(status)) return 'processed';
    return 'confirmed';
  }

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        // Fetch order details
        const response = await authService.getOrders();
        const orderData = (response.data || []).find(o => o.order_id === orderId);
        
        if (!orderData) {
          throw new Error('Order not found');
        }

        setOrder(orderData);
        
        // Find the specific item
        const itemData = (orderData.items || []).find(i => 
          (i.product_id === itemHandle || i.product_handle === itemHandle || i.name === itemHandle) &&
          (i.variant_id === itemVariantId || i.variant_title === itemVariantId || !itemVariantId)
        ) || (orderData.items && orderData.items[0]);

        if (!itemData) {
          throw new Error('Item not found');
        }

        setItem(itemData);
      } catch (error) {
        console.error('Error loading order item:', error);
        setError(error.message || 'Failed to load order item');
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, [orderId, itemHandle, itemVariantId, navigate]);

  useEffect(() => {
    console.log('useEffect [item] triggered. item:', item);
    const productHandle = item?.handle || item?.product_id;
    console.log('Resolved product handle for fetch:', productHandle);
    if (!productHandle) return;
    setProductLoading(true);
    setProductError('');
    fetch(`${API_BASE_URL}/products/handle/${productHandle}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch product details');
        return res.json();
      })
      .then(data => {
        console.log('Product details fetched:', data);
        setProductDetails(data);
      })
      .catch(err => {
        setProductError(err.message || 'Failed to fetch product details');
      })
      .finally(() => setProductLoading(false));
  }, [item]);

  // Debug: Log productDetails whenever it changes
  useEffect(() => {
    if (productDetails !== null) {
      console.log('productDetails state updated:', productDetails);
    }
  }, [productDetails]);

  if (loading) {
    return (
      <div className="dashboard-root">
        <TopMenu pageTitle="Order Item Details" email={email} onLogout={onLogout} />
        <div className="dashboard-container">
          <div className="fulfillment-loading">
            <div className="loader"></div>
            <p>Loading order item details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-root">
        <TopMenu pageTitle="Order Item Details" email={email} onLogout={onLogout} />
        <div className="dashboard-container">
          <div className="fulfillment-error">
            <p>{error}</p>
            <button onClick={() => navigate('/orders')}>Back to Orders</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Order Item Details" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="order-item-details">
          <div className="back-button" onClick={() => navigate('/orders')} style={{ marginBottom: '20px' }}>
           Back to Orders
          </div>

          <div className="details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
            {/* Left Column: Item Details */}
            <div className="item-details">
              <h2 style={{ marginBottom: '20px' }}>Item Details</h2>
              
              <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
                {/* Product Image */}
                <div style={{ flexShrink: 0, width: '200px', height: '200px' }}>
                  {item.image ? (
                    <img 
                      src={item.image} 
                      alt={item.name}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        borderRadius: '8px' 
                      }}
                    />
                  ) : (
                    <div style={{ 
                      width: '100%', 
                      height: '100%', 
                      backgroundColor: '#f5f5f5', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      borderRadius: '8px',
                      color: '#aaa',
                      fontSize: '14px'
                    }}>
                      No Image
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '20px' }}>{item.name}</h3>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    <p style={{ margin: '0 0 10px 0' }}>
                      <strong>Variant:</strong> {item.variant_title || 'Default'}
                    </p>
                    <p style={{ margin: '0 0 10px 0' }}>
                      <strong>Quantity:</strong> {item.quantity}
                    </p>
                    <p style={{ margin: '0 0 10px 0' }}>
                      <strong>Price:</strong> ₹{item.price}
                    </p>
                    {item.product?.vendor && (
                      <p style={{ margin: '0 0 10px 0' }}>
                        <strong>Brand:</strong> {item.product.vendor}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Product Description */}
              {item.product?.body_html && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Product Description</h3>
                  <div 
                    style={{ 
                      fontSize: '14px',
                      lineHeight: '1.6',
                      color: '#666'
                    }}
                    dangerouslySetInnerHTML={{ __html: item.product.body_html }}
                  />
                </div>
              )}

              {/* Product Attributes */}
              {item.product?.visual_attributes && (
                <div>
                  <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Product Attributes</h3>
                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '15px',
                    fontSize: '14px',
                    color: '#666'
                  }}>
                    {item.product.visual_attributes.style && (
                      <div>
                        <p style={{ margin: '0 0 5px 0', fontWeight: '500' }}>Style</p>
                        <p style={{ margin: '0' }}>
                          {item.product.visual_attributes.style.primary}
                          {item.product.visual_attributes.style.specific && 
                            ` - ${item.product.visual_attributes.style.specific}`}
                        </p>
                      </div>
                    )}
                    {item.product.visual_attributes.occasion && (
                      <div>
                        <p style={{ margin: '0 0 5px 0', fontWeight: '500' }}>Occasion</p>
                        <p style={{ margin: '0' }}>
                          {item.product.visual_attributes.occasion.primary}
                          {item.product.visual_attributes.occasion.specific && 
                            ` - ${item.product.visual_attributes.occasion.specific}`}
                        </p>
                      </div>
                    )}
                    {item.product.visual_attributes.pattern && (
                      <div>
                        <p style={{ margin: '0 0 5px 0', fontWeight: '500' }}>Pattern</p>
                        <p style={{ margin: '0' }}>
                          {item.product.visual_attributes.pattern.primary}
                          {item.product.visual_attributes.pattern.specific && 
                            ` - ${item.product.visual_attributes.pattern.specific}`}
                        </p>
                      </div>
                    )}
                    {item.product.visual_attributes.colors && (
                      <div>
                        <p style={{ margin: '0 0 5px 0', fontWeight: '500' }}>Color</p>
                        <p style={{ margin: '0' }}>
                          {item.product.visual_attributes.colors.primary_color}
                          {item.product.visual_attributes.colors.palette && 
                            ` (${item.product.visual_attributes.colors.palette} palette)`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action UI below item details */}
              {(() => {
                const step = getStepKey(item);
                if (step === 'confirmed') {
                  return (
                    <form onSubmit={handleConfirmedSubmit} style={{ marginTop: 32, background: '#fafafa', padding: 20, borderRadius: 8, boxShadow: '0 1px 4px #eee' }}>
                      <div style={{ marginBottom: 18 }}>
                        <span style={{ fontWeight: 500, fontSize: 15, marginRight: 8 }}>Product Link:</span>
                        {productLoading ? (
                          <span style={{ color: '#888', fontSize: 15 }}>Loading...</span>
                        ) : productError ? (
                          <span style={{ color: '#d32f2f', fontSize: 15 }}>{productError}</span>
                        ) : productDetails && (productDetails.url || productDetails.alt_url) ? (
                          <>
                            {productDetails.url && (
                              <button
                                onClick={handleProductLinkOpen}
                                type="button"
                                className="action-button"
                                style={{ color: '#fff', textDecoration: 'none', fontSize: 12, marginRight: 8 }}
                              >
                                Click here
                              </button>
                            )}
                            {productDetails.alt_url && (
                              <button
                                onClick={handleAltProductLinkOpen}
                                type="button"
                                className="action-button"
                                style={{ color: '#fff', textDecoration: 'none', fontSize: 12 }}
                              >
                                Alt Link
                              </button>
                            )}
                          </>
                        ) : (
                          <span style={{ color: '#888', fontSize: 15 }}>Not available</span>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 18, columnGap: 16, alignItems: 'center', marginBottom: 18 }}>
                        <label style={{ fontWeight: 500, fontSize: 15 }}>Purchase Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={purchaseAmount}
                          onChange={e => setPurchaseAmount(e.target.value)}
                          placeholder="Purchase Amount"
                          style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', width: '96%' }}
                        />
                      </div>
                      {formError && <div style={{ color: '#d32f2f', marginBottom: 8 }}>{formError}</div>}
                      <button type="submit" disabled={formLoading} style={{ padding: '10px 20px', borderRadius: 4, border: 'none', background: '#000', color: '#fff', cursor: 'pointer', fontWeight: 500 }}>
                        {formLoading ? 'Submitting...' : 'Submit'}
                      </button>
                    </form>
                  );
                }
                if (step === 'processed') {
                  return (
                    <form onSubmit={handleProcessedSubmit} style={{ marginTop: 32, background: '#fafafa', padding: 20, borderRadius: 8, boxShadow: '0 1px 4px #eee' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                        <input
                          type="text"
                          value={awb}
                          onChange={e => setAwb(e.target.value)}
                          placeholder="AWB Number"
                          style={{ flex: 1, padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                        />
                      </div>
                      {formError && <div style={{ color: '#d32f2f', marginBottom: 8 }}>{formError}</div>}
                      <button type="submit" disabled={formLoading} style={{ padding: '10px 20px', borderRadius: 4, border: 'none', background: '#000', color: '#fff', cursor: 'pointer', fontWeight: 500 }}>
                        {formLoading ? 'Submitting...' : 'Submit'}
                      </button>
                    </form>
                  );
                }
                if (step === 'dispatched') {
                  return (
                    <div style={{ marginTop: 32 }}>
                      <button onClick={handleMarkDelivered} disabled={formLoading} style={{ padding: '12px 32px', borderRadius: 4, border: 'none', background: '#000', color: '#fff', cursor: 'pointer', fontWeight: 500, fontSize: 16 }}>
                        {formLoading ? 'Marking...' : 'Mark as Delivered'}
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Right Column: Order & Shipping Info */}
            <div className="order-info">
              <OrderStatusTracker item={item} order={order} />
              <h2 style={{ marginBottom: '20px' }}>Order Information</h2>
              
              {/* Order Details */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Order Details</h3>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <p style={{ margin: '0 0 10px 0' }}>
                    <strong>Order ID:</strong> {order.order_id}
                  </p>
                  <p style={{ margin: '0 0 10px 0' }}>
                    <strong>Order Date:</strong> {new Date(order.created_at).toLocaleString()}
                  </p>
                  <p style={{ margin: '0 0 10px 0' }}>
                    <strong>Status:</strong> <span style={{ textTransform: 'capitalize' }}>{order.status || 'Unfulfilled'}</span>
                  </p>
                  {order.note && (
                    <p style={{ margin: '0 0 10px 0' }}>
                      <strong>Note:</strong> {order.note}
                    </p>
                  )}
                </div>
              </div>

              {/* Shipping Information */}
              {order.shipping_address && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Shipping Information</h3>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    <p style={{ margin: '0 0 10px 0' }}>
                      <strong>Name:</strong> {order.shipping_address.name}
                    </p>
                    <p style={{ margin: '0 0 10px 0', lineHeight: '1.5' }}>
                      <strong>Address:</strong><br />
                      {order.shipping_address.address1}<br />
                      {order.shipping_address.address2 && `${order.shipping_address.address2}`}<br />
                      {order.shipping_address.city}, {order.shipping_address.pincode} {order.shipping_address.state}
                    </p>
                    {order.shipping_address.phone && (
                      <p style={{ margin: '0 0 10px 0' }}>
                        <strong>Phone:</strong> {order.shipping_address.phone}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Shipping Status */}
              {item.shipping_info && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Shipping Status</h3>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {item.shipping_info.tracking_info?.awb_code && (
                      <p style={{ margin: '0 0 10px 0' }}>
                        <strong>AWB Number:</strong> {item.shipping_info.tracking_info.awb_code}
                      </p>
                    )}
                    {item.shipping_info.tracking_info?.courier_name && (
                      <p style={{ margin: '0 0 10px 0' }}>
                        <strong>Courier:</strong> {item.shipping_info.tracking_info.courier_name}
                      </p>
                    )}
                    {item.delivered_details && (
                      <p style={{ margin: '0 0 10px 0' }}>
                        <strong>Delivered On:</strong> {new Date(item.delivered_details.delivered_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Information */}
              {order.payment_details && (
                <div>
                  <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Payment Information</h3>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    <p style={{ margin: '0 0 10px 0' }}>
                      <strong>Payment Method:</strong> {order.payment_details.payment_method || 'N/A'}
                    </p>
                    <p style={{ margin: '0 0 10px 0' }}>
                      <strong>Amount:</strong> ₹{order.payment_details.amount || '0'}
                    </p>
                    <p style={{ margin: '0 0 10px 0' }}>
                      <strong>Status:</strong> <span style={{ textTransform: 'capitalize' }}>{order.payment_details.status || 'Unknown'}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderItemDetails; 